import { useEffect, useMemo } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
  availableAfesAtom,
  triggerBulkAvailableAfesFetchAtom,
} from 'pages/packages/show/evaluation-result/atoms';
import type { AvailableAfe } from 'types/api-responses';
import type { BulkEditAfeOptions } from './types';

/**
 * Loads the AFE options that are valid for ALL provided slot IDs by reading
 * each slot's `availableAfesAtom` and intersecting by AFE id. Triggers the
 * lazy fetch for any slot that hasn't been requested yet, and re-fetches on
 * error. The result is reactive — pass live `slotIds` and the hook will
 * recompute when the selection changes.
 *
 * @param {string[]} slotIds - The slot IDs whose available AFEs to intersect.
 * @return {BulkEditAfeOptions} The aggregate status + intersected item list.
 */
export function useBulkEditAfeOptions(slotIds: string[]): BulkEditAfeOptions {
  const triggerBulkFetch = useSetAtom(triggerBulkAvailableAfesFetchAtom);

  // Stable, deduped, sorted id list — used as the deps key everywhere below.
  const ids = useMemo(
    () => [...new Set(slotIds.filter(Boolean))].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slotIds.join('|')]
  );

  // Trigger the lazy fetch for any slot that's still idle (no trigger yet)
  // or in an error state. The write atom handles per-slot state inspection
  // and is idempotent for already-loading / loaded slots.
  useEffect(() => {
    triggerBulkFetch(ids);
  }, [ids, triggerBulkFetch]);

  // Derived atom that reads every slot's loadable state and computes the
  // intersection (or surfaces loading/error). Built per id list so it only
  // depends on the slots in scope.
  const aggregateAtom = useMemo(
    () =>
      atom<BulkEditAfeOptions>((get) => {
        if (ids.length === 0) {
          return { status: 'empty', items: [] };
        }
        const states = ids.map((id) => get(availableAfesAtom({ slotId: id })));

        if (
          states.some(
            (s) =>
              s.state === 'loading' ||
              (s.state === 'hasData' && s.data === null)
          )
        ) {
          return { status: 'loading', items: [] };
        }

        const err = states.find((s) => s.state === 'hasError');
        if (err && err.state === 'hasError') {
          const errorMessage =
            err.error instanceof Error
              ? err.error.message
              : 'Failed to load available AFEs';
          return { status: 'error', items: [], errorMessage };
        }

        const lists: AvailableAfe[][] = states.map((s) => {
          if (s.state === 'hasData' && s.data !== null) {
            return s.data.availableAfes ?? [];
          }
          return [];
        });
        const [first, ...rest] = lists;
        const items =
          first?.filter((a) =>
            rest.every((list) => list.some((b) => b.id === a.id))
          ) ?? [];

        return { status: items.length ? 'ready' : 'empty', items };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids.join('|')]
  );

  return useAtomValue(aggregateAtom);
}
